import React, { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Gift, Loader2, Plus, Save, Trash2 } from "lucide-react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { PageContainer, PageHeader, Card, Pill } from "@/components/layout/PageShell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useAuth } from "@/contexts/AuthContext";
import api from "@/lib/api";

const emptyRule = {
  name: "",
  rule_type: "nationality",
  rule_value: "",
  bonus_points: "0",
  extra_earn_percent: "0",
  is_active: true,
};

export default function LoyaltySettingsPage() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const branchId = user?.default_branch?.id;
  const branchName = user?.default_branch?.name || "selected branch";

  const [form, setForm] = useState(null);
  const [ruleForm, setRuleForm] = useState(emptyRule);
  const [editingRuleId, setEditingRuleId] = useState(null);

  const { data, isLoading } = useQuery({
    queryKey: ["loyalty-settings", branchId],
    queryFn: async () => {
      const { data: res } = await api.get("/loyalty/settings", {
        params: branchId ? { branch_id: branchId } : {},
      });
      return res;
    },
    enabled: Boolean(user),
  });

  useEffect(() => {
    if (data?.settings && !form) setForm({ ...data.settings });
  }, [data, form]);

  const saveMutation = useMutation({
    mutationFn: async (payload) => {
      const { data: res } = await api.put("/loyalty/settings", {
        branch_id: branchId,
        ...payload,
      });
      return res;
    },
    onSuccess: (res) => {
      toast.success(res.message);
      setForm({ ...res.settings });
      queryClient.invalidateQueries({ queryKey: ["loyalty-settings", branchId] });
    },
    onError: (err) => toast.error(err.response?.data?.message || "Could not save settings."),
  });

  const saveRuleMutation = useMutation({
    mutationFn: async (payload) => {
      const { data: res } = await api.post("/loyalty/segment-rules", {
        branch_id: branchId,
        id: editingRuleId || undefined,
        ...payload,
      });
      return res;
    },
    onSuccess: (res) => {
      toast.success(res.message);
      setRuleForm(emptyRule);
      setEditingRuleId(null);
      queryClient.invalidateQueries({ queryKey: ["loyalty-settings", branchId] });
    },
    onError: (err) => toast.error(err.response?.data?.message || "Could not save rule."),
  });

  const deleteRuleMutation = useMutation({
    mutationFn: async (id) => {
      await api.delete(`/loyalty/segment-rules/${id}`, {
        params: { branch_id: branchId },
      });
    },
    onSuccess: () => {
      toast.success("Rule deleted.");
      queryClient.invalidateQueries({ queryKey: ["loyalty-settings", branchId] });
    },
  });

  if (isLoading || !form) {
    return (
      <PageContainer>
        <div className="flex items-center gap-2 text-muted-foreground py-12">
          <Loader2 className="h-5 w-5 animate-spin" />
          Loading loyalty settings…
        </div>
      </PageContainer>
    );
  }

  const rules = data?.segment_rules ?? [];

  return (
    <PageContainer>
      <PageHeader
        title={t("loyalty.title", "Loyalty & CRM")}
        subtitle={t("loyalty.subtitle", "Points accrual, redemption, and customer segments for {{branch}}.", {
          branch: branchName,
        })}
      />

      <div className="mb-4">
        <Pill tone={form.is_enabled ? "green" : "slate"}>
          {form.is_enabled ? "Loyalty enabled" : "Loyalty disabled"}
        </Pill>
      </div>

      <Tabs defaultValue="program">
        <TabsList className="mb-4">
          <TabsTrigger value="program">Program rules</TabsTrigger>
          <TabsTrigger value="segments">Segment promotions</TabsTrigger>
        </TabsList>

        <TabsContent value="program">
          <Card className="p-4 max-w-2xl space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <Label>Enable loyalty program</Label>
                <p className="text-sm text-muted-foreground">
                  Earn and redeem points when a customer is on the bill. Hide redeem / usual on POS via Administration → General Settings → Show loyalty on POS.
                </p>
              </div>
              <Switch
                checked={form.is_enabled}
                onCheckedChange={(v) => setForm((f) => ({ ...f, is_enabled: v }))}
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Spend per 1 point</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={form.spend_per_point}
                  onChange={(e) => setForm({ ...form, spend_per_point: e.target.value })}
                />
                <p className="text-xs text-muted-foreground mt-1">e.g. 10 = 1 point per 10 currency units paid</p>
              </div>
              <div>
                <Label>Value per point (redemption)</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={form.point_value}
                  onChange={(e) => setForm({ ...form, point_value: e.target.value })}
                />
              </div>
              <div>
                <Label>Minimum redeem points</Label>
                <Input
                  type="number"
                  value={form.min_redeem_points}
                  onChange={(e) => setForm({ ...form, min_redeem_points: e.target.value })}
                />
              </div>
              <div>
                <Label>Birthday month bonus points</Label>
                <Input
                  type="number"
                  value={form.birthday_bonus_points}
                  onChange={(e) => setForm({ ...form, birthday_bonus_points: e.target.value })}
                />
              </div>
            </div>
            <Button
              onClick={() =>
                saveMutation.mutate({
                  ...form,
                  spend_per_point: Number(form.spend_per_point),
                  point_value: Number(form.point_value),
                  min_redeem_points: Number(form.min_redeem_points),
                  birthday_bonus_points: Number(form.birthday_bonus_points),
                })
              }
              disabled={saveMutation.isPending}
            >
              {saveMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
              Save program
            </Button>
          </Card>
        </TabsContent>

        <TabsContent value="segments">
          <div className="grid gap-4 lg:grid-cols-2">
            <Card className="p-4 space-y-3">
              <h3 className="font-semibold flex items-center gap-2">
                <Gift className="h-4 w-4" />
                {editingRuleId ? "Edit segment rule" : "New segment rule"}
              </h3>
              <div>
                <Label>Name</Label>
                <Input value={ruleForm.name} onChange={(e) => setRuleForm({ ...ruleForm, name: e.target.value })} />
              </div>
              <div>
                <Label>Rule type</Label>
                <Select
                  value={ruleForm.rule_type}
                  onValueChange={(v) => setRuleForm({ ...ruleForm, rule_type: v })}
                >
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="nationality">Nationality match</SelectItem>
                    <SelectItem value="min_visits">Minimum visits</SelectItem>
                    <SelectItem value="birthday_month">Birthday month</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Rule value</Label>
                <Input
                  value={ruleForm.rule_value}
                  onChange={(e) => setRuleForm({ ...ruleForm, rule_value: e.target.value })}
                  placeholder={ruleForm.rule_type === "min_visits" ? "10" : "UAE or month 1-12"}
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Bonus points (one-time per bill)</Label>
                  <Input
                    type="number"
                    value={ruleForm.bonus_points}
                    onChange={(e) => setRuleForm({ ...ruleForm, bonus_points: e.target.value })}
                  />
                </div>
                <div>
                  <Label>Extra earn %</Label>
                  <Input
                    type="number"
                    value={ruleForm.extra_earn_percent}
                    onChange={(e) => setRuleForm({ ...ruleForm, extra_earn_percent: e.target.value })}
                  />
                </div>
              </div>
              <div className="flex gap-2">
                <Button
                  onClick={() =>
                    saveRuleMutation.mutate({
                      name: ruleForm.name,
                      rule_type: ruleForm.rule_type,
                      rule_value: ruleForm.rule_value || null,
                      bonus_points: Number(ruleForm.bonus_points) || 0,
                      extra_earn_percent: Number(ruleForm.extra_earn_percent) || 0,
                      is_active: ruleForm.is_active,
                    })
                  }
                  disabled={!ruleForm.name || saveRuleMutation.isPending}
                >
                  Save rule
                </Button>
                {editingRuleId && (
                  <Button variant="outline" onClick={() => { setEditingRuleId(null); setRuleForm(emptyRule); }}>
                    Cancel
                  </Button>
                )}
              </div>
            </Card>
            <Card className="p-4">
              <h3 className="font-semibold mb-3">Active rules</h3>
              <div className="space-y-2">
                {rules.map((rule) => (
                  <div key={rule.id} className="border rounded-lg p-3 flex justify-between gap-2">
                    <div>
                      <div className="font-medium">{rule.name}</div>
                      <div className="text-sm text-muted-foreground">
                        {rule.rule_type} · {rule.rule_value || "—"}
                        {rule.bonus_points > 0 && ` · +${rule.bonus_points} pts`}
                        {rule.extra_earn_percent > 0 && ` · +${rule.extra_earn_percent}% earn`}
                      </div>
                    </div>
                    <div className="flex gap-1">
                      <Button
                        size="icon"
                        variant="ghost"
                        onClick={() => {
                          setEditingRuleId(rule.id);
                          setRuleForm({
                            name: rule.name,
                            rule_type: rule.rule_type,
                            rule_value: rule.rule_value || "",
                            bonus_points: String(rule.bonus_points),
                            extra_earn_percent: String(rule.extra_earn_percent),
                            is_active: rule.is_active,
                          });
                        }}
                      >
                        <Save className="h-4 w-4" />
                      </Button>
                      <Button
                        size="icon"
                        variant="ghost"
                        onClick={() => deleteRuleMutation.mutate(rule.id)}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                ))}
                {rules.length === 0 && (
                  <p className="text-sm text-muted-foreground">No segment rules yet.</p>
                )}
              </div>
            </Card>
          </div>
        </TabsContent>
      </Tabs>
    </PageContainer>
  );
}
